import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase';

const { 
  FiUsers, FiCheckCircle, FiBookOpen, FiSearch, 
  FiFilter, FiMail, FiPhone, FiExternalLink, FiEdit2,
  FiCalendar
} = FiIcons;

const COURSE_STATUSES = [
  'Ny interesse',
  'Kontaktet',
  'Informasjon sendt',
  'Påmeldt',
  'Gjennomført',
  'Ikke interessert'
];

export default function CourseInterestList() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Alle');
  const [stats, setStats] = useState({ total: 0, enrolled: 0, completed: 0 });

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('utredere')
        .select('*')
        .eq('qualification_course_interest', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLeads(data || []);
      calculateStats(data || []);
    } catch (err) {
      console.error('Error fetching leads:', err);
      alert('Kunne ikke hente kursinteresserte');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    setStats({
      total: data.length,
      enrolled: data.filter(l => l.course_status === 'Påmeldt').length,
      completed: data.filter(l => l.course_status === 'Gjennomført').length
    });
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('utredere')
        .update({ course_status: newStatus })
        .eq('id', id);

      if (error) throw error;
      
      const updatedLeads = leads.map(l => l.id === id ? { ...l, course_status: newStatus } : l);
      setLeads(updatedLeads);
      calculateStats(updatedLeads);
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Kunne ikke oppdatere status');
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'Alle' || lead.course_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kursinteresserte utredere</h1>
          <p className="text-slate-500">Oppfølging av leads til HMS Varsling Utrederkvalifisering (5 000 kr eks. mva.)</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
              <SafeIcon icon={FiUsers} className="text-xl" />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium">Kursinteresserte</p>
              <h3 className="text-2xl font-bold text-slate-900">{stats.total}</h3>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center text-amber-600">
              <SafeIcon icon={FiBookOpen} className="text-xl" />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium">Påmeldt</p>
              <h3 className="text-2xl font-bold text-slate-900">{stats.enrolled}</h3>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
              <SafeIcon icon={FiCheckCircle} className="text-xl" />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium">Gjennomført</p>
              <h3 className="text-2xl font-bold text-slate-900">{stats.completed}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Søk på navn, e-post eller firma..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <SafeIcon icon={FiFilter} className="text-slate-400" />
          <select
            className="border border-slate-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none bg-white min-w-[160px]"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="Alle">Alle statuser</option>
            {COURSE_STATUSES.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-4 text-sm font-semibold text-slate-700">Utreder / Firma</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-700">Kontaktinfo</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-700">Søknadsstatus</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-700">Registrert</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-700">Kursstatus</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-700">Handlinger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-900">{lead.full_name}</div>
                    <div className="text-sm text-slate-500">{lead.company_name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <a href={`mailto:${lead.email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                        <SafeIcon icon={FiMail} size={12} /> {lead.email}
                      </a>
                      {lead.phone && (
                        <div className="text-sm text-slate-500 flex items-center gap-1">
                          <SafeIcon icon={FiPhone} size={12} /> {lead.phone}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      lead.active ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {lead.active ? 'Godkjent' : 'Venter'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    <div className="flex items-center gap-1">
                      <SafeIcon icon={FiCalendar} size={14} />
                      {new Date(lead.created_at).toLocaleDateString('no-NO')}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={lead.course_status || 'Ny interesse'}
                      onChange={(e) => updateStatus(lead.id, e.target.value)}
                      className={`text-xs font-semibold px-2 py-1 rounded border outline-none ${
                        lead.course_status === 'Påmeldt' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        lead.course_status === 'Gjennomført' ? 'bg-green-50 text-green-700 border-green-200' :
                        lead.course_status === 'Kontaktet' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                        'bg-slate-50 text-slate-700 border-slate-200'
                      }`}
                    >
                      {COURSE_STATUSES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => updateStatus(lead.id, 'Kontaktet')}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Marker som kontaktet"
                      >
                        <SafeIcon icon={FiEdit2} />
                      </button>
                      <a 
                        href={`mailto:${lead.email}?subject=HMS Varsling Utrederkvalifisering`}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Send e-post"
                      >
                        <SafeIcon icon={FiMail} />
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredLeads.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                    Ingen kursinteresserte utredere funnet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}